// Re-buckets the four dashboard gadgets' rows into the redesign's seven queues (docs/ui_redesign/02-status-model-and-queues.md).
// Pure functions, so they can be unit tested without the Dashboard component.
import { isRejected } from '../status';

export const QUEUES = [
	{ key: 'scan', label: 'Waiting to scan', tone: 'neutral', code: 'scan status: not processed · in progress' },
	{ key: 'unmatched', label: 'Unmatched', tone: 'warn', code: '10 · 20 · 30 · 35' },
	{ key: 'correct', label: 'Needs correction', tone: 'warn', code: '40 · 41 · 60' },
	{ key: 'approve', label: 'Pending approval', tone: 'info', code: '50 · approval levels · rejected' },
	{ key: 'ready', label: 'Ready to post', tone: 'ok', code: 'final level approved' },
	{ key: 'failed', label: 'Post failed', tone: 'bad', code: '180' },
	{ key: 'posted', label: 'Posted', tone: 'neutral', code: '170' }
];

// One line per queue, shown above its gadget.
export const GUIDE = {
	scan: 'Files waiting for the scanner. Upload invoices or drop them on the list, then scan them.',
	unmatched: 'Scanned, but no vendor yet. Open a row to map the vendor or to correct the invoice.',
	correct: 'Missing information and errors from the verifier. Open a row, fix it and save.',
	approve: 'Waiting on an approver, including invoices an approver rejected. Open a row to approve, reject or view it.',
	ready: 'Approved at the final level and ready to post.',
	failed: 'The post was rejected. Open the invoice to see why, fix it and repost.',
	posted: 'Posted invoices in the gadget’s date range.'
};

// Which classic gadget, and which list in the Dashboard state, each queue shows.
export const QUEUE_SOURCE = {
	scan: [1, 'queuedInvoices'], unmatched: [2, 'filteredUnmatchedInvoices'], correct: [2, 'filteredUnmatchedInvoices'],
	approve: [3, 'pendingApprovalInvoices'], ready: [4, 'approvedInvoices'], failed: [4, 'approvedInvoices'], posted: [4, 'approvedInvoices']
};

export const STAGES = [
	['Intake', ['scan', 'unmatched']],
	['Fix', ['correct']],
	['Approve', ['approve']],
	['Post', ['ready', 'failed']],
	['Done', ['posted']]
];

export { isRejected };
const sid = r => Number(r && r.invoiceDocumentStatusId);

// A queue row is [row, gadgetNumber]. Each queue maps to exactly one classic gadget, so its count matches what that
// gadget lists: rejected invoices stay with Pending Approval, as in Classic.
export function bucket({ queued = [], unmatched = [], pending = [], approved = [] }) {
	const tag = (rows, g) => rows.map(r => [r, g]);
	return {
		scan: tag(queued, 1),
		unmatched: tag(unmatched.filter(r => sid(r) < 40), 2),
		correct: tag(unmatched.filter(r => sid(r) >= 40), 2),
		approve: tag(pending, 3),
		ready: tag(approved.filter(r => sid(r) !== 170 && sid(r) !== 180), 4),
		failed: tag(approved.filter(r => sid(r) === 180), 4),
		posted: tag(approved.filter(r => sid(r) === 170), 4)
	};
}

// A manual upload stuck in "Scan in Progress" for more than 60 minutes is offered for scanning again (TopLeftGadget.js).
export const minutesSince = d => (d ? Math.abs(Date.now() - new Date(d).getTime()) / 60000 : 0);
export const isStuckScan = r => r.uploadStatus === 2 && !!r.scanDate && minutesSince(r.scanDate) > 60;

export function attention(buckets) {
	const items = [];
	const failed = buckets.failed.length;
	const stuck = buckets.scan.filter(([r]) => isStuckScan(r)).length;
	const rejected = buckets.approve.filter(([r]) => isRejected(sid(r))).length;
	if (failed) items.push({ key: 'failed', queue: 'failed', tone: 'bad', label: `${failed} post${failed > 1 ? 's' : ''} failed` });
	if (stuck) items.push({ key: 'stuck', queue: 'scan', tone: 'warn', label: `${stuck} scan${stuck > 1 ? 's' : ''} stuck over 60 minutes` });
	if (rejected) items.push({ key: 'rejected', queue: 'approve', tone: 'neutral', label: `${rejected} rejected` });
	return items;
}

// "For you": an unmatched or correction row routed to the signed-in user (the server lists the routed resource ids, joined
// by '|', and Classic's Routed To filter matches on them), or an approval the user can act on (the server sets
// approvalLinkText only then, and Classic opens such a row in Approval mode).
export function isMine(queue, row, userId) {
	if (!row) return false;
	if (queue === 'approve') return !!(row.approvalLinkText && String(row.approvalLinkText).length);
	if ((queue === 'unmatched' || queue === 'correct') && userId) {
		const me = String(userId).trim().toLowerCase();
		return String(row.routedToResourceId || '').split('|').some(id => id.trim().toLowerCase() === me);
	}
	return false;
}
export const MINE_QUEUES = ['unmatched', 'correct', 'approve'];

// Chips above a queue: one per status the queue holds (server label, status order), plus For you, Rejected and
// Partially posted. ctx: { isPartiallyPosted(row), isMine(row) }.
export function queueChips(queue, rows, ctx = {}) {
	const { isPartiallyPosted = () => false, isMine: mine = () => false } = ctx;
	if (queue === 'scan' || queue === 'failed') return [];
	const map = new Map();
	rows.forEach(r => {
		const k = sid(r);
		if (queue === 'approve' && isRejected(k)) return;
		const prev = map.get(k);
		map.set(k, { key: 's' + k, label: (prev && prev.label) || r.pendingStatus || r.status || String(k), count: (prev ? prev.count : 0) + 1, sid: k });
	});
	const chips = [...map.values()].sort((a, b) => a.sid - b.sid);
	const rejected = queue === 'approve' ? rows.filter(r => isRejected(sid(r))).length : 0;
	if (rejected) chips.push({ key: 'rejected', label: 'Rejected', count: rejected });
	const partial = queue === 'posted' ? rows.filter(isPartiallyPosted).length : 0;
	if (partial) chips.push({ key: 'partial', label: 'Partially posted', count: partial });
	const yours = MINE_QUEUES.includes(queue) ? rows.filter(mine).length : 0;
	const list = chips.length > 1 || rejected || partial ? chips : [];
	return yours ? [{ key: 'mine', label: 'For you', count: yours }, ...list] : list;
}

export function chipMatches(chip, row, ctx = {}) {
	const { isPartiallyPosted = () => false, isMine: mine = () => false } = ctx;
	if (!chip) return true;
	if (chip === 'mine') return mine(row);
	if (chip === 'rejected') return isRejected(sid(row));
	if (chip === 'partial') return isPartiallyPosted(row);
	return 's' + sid(row) === chip;
}

// How long each queue's rows have waited, from a date the gadget row already carries: the upload, the scan, the
// invoice date (the Pending Approval rows have no other date) and the approval.
export const AGE_FIELD = {
	scan: 'documentManualUploadFileUpdateDate', unmatched: 'invoiceDocumentScanUpdateDate', correct: 'invoiceDocumentScanUpdateDate',
	approve: 'invoiceDate', ready: 'approvalRejectionUpdateDate', failed: 'approvalRejectionUpdateDate', posted: 'approvalRejectionUpdateDate'
};
export const OLD_DAYS = 7;
const DAY = 86400000;

export function ageMs(queue, row, now = Date.now()) {
	const d = row && row[AGE_FIELD[queue]];
	const t = d ? new Date(d).getTime() : NaN;
	return Number.isFinite(t) ? Math.max(0, now - t) : null;
}

export function formatAge(ms) {
	if (ms == null) return '';
	const min = Math.floor(ms / 60000);
	if (min < 60) return `${Math.max(1, min)} min`;
	const h = Math.floor(min / 60);
	return h < 24 ? `${h} h` : `${Math.floor(h / 24)} d`;
}

// { oldest, over }: the oldest row's age, and how many rows are older than OLD_DAYS. null when no row has the date.
export function queueAge(queue, rows, now = Date.now()) {
	let oldest = null;
	let over = 0;
	rows.forEach(r => {
		const a = ageMs(queue, r, now);
		if (a == null) return;
		if (oldest == null || a > oldest) oldest = a;
		if (a >= OLD_DAYS * DAY) over++;
	});
	return oldest == null ? null : { oldest, over };
}

// Totals per currency. The Unmatched/Discrepant rows carry no currency, so their amounts are never added up.
// format(n) formats one amount. null when any row has no currency.
export function amountSummary(rows, format = String) {
	if (!rows.length || rows.some(r => !r.currencyCode)) return null;
	const by = new Map();
	rows.forEach(r => by.set(r.currencyCode, (by.get(r.currencyCode) || 0) + (Number(r.totalAmount != null ? r.totalAmount : r.totalInvoiceAmount) || 0)));
	const list = [...by.entries()].sort((a, b) => b[1] - a[1]);
	const top = `${format(list[0][1])} ${list[0][0]}`;
	return { text: list.length === 1 ? top : `${top} + ${list.length - 1} more`, parts: list };
}

// The tab the dashboard opens when none is remembered: the first queue with work, in the order people usually clear them.
export const DEFAULT_ORDER = ['correct', 'unmatched', 'approve', 'failed', 'ready', 'scan', 'posted'];
export const defaultQueue = buckets => DEFAULT_ORDER.find(k => ((buckets && buckets[k]) || []).length > 0) || null;

// The period a gadget lists: its weeks filter (0 = no filter).
export const periodLabel = weeks => {
	const w = Number(weeks);
	if (!Number.isFinite(w) || w < 0) return '';
	if (w === 0) return 'All dates';
	return w === 1 ? 'Last week' : `Last ${w} weeks`;
};
